import React from "react";
import { FieldArray, Field, Form, FormikProps } from "formik";
import {
  Button,
  LinearProgress,
  NoSsr,
  Grid,
  CardContent,
  Typography
} from "@material-ui/core";
import { Help } from "@material-ui/icons";

import { IDevice } from "../models/IDevice";
import { Validators } from "../../util/validators";
import { StyledTextField, TextField } from "../../components/TextField";
import {
  AutocompleteSelect,
  StyledAutocomplete
} from "../../components/AutocompleteSelect";
import {
  DevicePayloadFieldParametersFormArray,
  DeviceBasicCalculationParametersFormArray,
  DevicePayloadFormatFormArray,
  DeviceValueParametersFormArray,
  DeviceDownlinkFormatFormArray
} from "./DeviceParameters";
import {
  StyledTitle,
  StyledPayloadFormatsCard,
  StyledAutocompleteSelectLabel
} from "../../components/sharedStyledComponents";
import { initialDevice } from "../models/IDevice";
import {
  IDeviceModel,
  initialDeviceModel
} from "../../DeviceModels/models/IDeviceModel";
import {
  initialSubscription,
  ISubscription
} from "../../Subscriptions/models/ISubscription";

interface IEditDeviceProps {
  onManufacturerChange: (sub: ISubscription, deviceFormValue: IDevice) => void;
}

type CreateDeviceFormType = FormikProps<IDevice> & IEditDeviceProps;

export const CreateDeviceForm: React.FunctionComponent<
  CreateDeviceFormType
> = ({
  isSubmitting,
  values,
  submitForm,
  setFieldValue,
  onManufacturerChange
}: CreateDeviceFormType) => {
  const {
    isLoading,
    deviceModelList,
    subscriptionList,
    manufacturersList
  } = values;

  const {
    serial,
    name,
    note,
    deviceModel,
    subscription,
    manufacturer
  } = Validators.editDevice;

  const deviceModelHandleChange = (selectedValue: IDeviceModel) => {
    if (selectedValue === null) {
      setFieldValue("deviceModel", initialDevice);
      setFieldValue(
        "payloadFieldProperties",
        initialDevice.payloadFieldProperties
      );
      setFieldValue(
        "basicCalculationProperties",
        initialDevice.basicCalculationProperties
      );
      setFieldValue("payloadFormats", initialDevice.payloadFormats);
      setFieldValue("valueParameters", initialDevice.valueParameters);
      setFieldValue("downlinkFormats", initialDevice.downlinkFormats);
    } else {
      setFieldValue("deviceModel", selectedValue);
      setFieldValue(
        "payloadFieldProperties",
        selectedValue[`payloadFieldProperties`]
      );
      setFieldValue(
        "basicCalculationProperties",
        selectedValue[`basicCalculationProperties`]
      );
      setFieldValue("payloadFormats", selectedValue[`payloadFormats`]);
      setFieldValue("valueParameters", selectedValue[`valueParameters`]);
      setFieldValue("downlinkFormats", selectedValue[`downlinkFormats`]);
    }
  };

  const manufacturerHandleChange = (selectedManufacturer: ISubscription) => {
    if (selectedManufacturer === null) {
      setFieldValue("manufacturer", initialSubscription);
      setFieldValue("deviceModel", initialDeviceModel);
      setFieldValue("deviceModelList", []);
    } else {
      onManufacturerChange(selectedManufacturer, values);
    }
  };

  return (
    <Form>
      <StyledTextField
        type="text"
        label="Serial"
        name="serial"
        multiline={false}
        disabled={Boolean(values.id) || isSubmitting || isLoading}
        component={TextField}
        validate={serial}
      />
      <StyledTextField
        type="text"
        label="Name"
        name="name"
        multiline={false}
        disabled={isSubmitting || isLoading}
        component={TextField}
        validate={name}
      />
      <br />
      <StyledTextField
        type="text"
        label="Note"
        name="note"
        multiline={true}
        disabled={isSubmitting || isLoading}
        component={TextField}
        validate={note}
      />

      <br />
      <br />

      <NoSsr>
        {Boolean(values.manufacturer.id) && !isLoading && (
          <StyledAutocompleteSelectLabel htmlFor="manufacturer">
            Manufacturer
          </StyledAutocompleteSelectLabel>
        )}

        <StyledAutocomplete>
          <Field
            name="manufacturer"
            options={manufacturersList}
            component={AutocompleteSelect}
            isDisabled={isSubmitting || isLoading}
            validate={manufacturer}
            labelField="name"
            identityKey="id"
            placeholder="Select Manufacturer"
            isClearable={true}
            clearValue={initialDevice}
            onChange={manufacturerHandleChange}
          />
        </StyledAutocomplete>
      </NoSsr>

      <br />
      <br />

      <NoSsr>
        {Boolean(values.deviceModel.id) && !isLoading && (
          <StyledAutocompleteSelectLabel htmlFor="deviceModel">
            Model
          </StyledAutocompleteSelectLabel>
        )}

        <StyledAutocomplete>
          <Field
            name="deviceModel"
            options={deviceModelList}
            component={AutocompleteSelect}
            isDisabled={
              isSubmitting ||
              isLoading ||
              Boolean(values.deviceModelListIsLoading) === true
            }
            validate={deviceModel}
            labelField="name"
            identityKey="id"
            placeholder="Select Model"
            isClearable={true}
            clearValue={initialDeviceModel}
            onChange={deviceModelHandleChange}
          />
          {Boolean(values.deviceModelListIsLoading) === true && (
            <LinearProgress />
          )}
        </StyledAutocomplete>
      </NoSsr>
      <br />
      <br />
      <NoSsr>
        {Boolean(values.subscription.id) && !isLoading && (
          <StyledAutocompleteSelectLabel htmlFor="Subscription">
            Subscription
          </StyledAutocompleteSelectLabel>
        )}

        <StyledAutocomplete>
          <Field
            name="subscription"
            options={subscriptionList}
            component={AutocompleteSelect}
            isDisabled={isSubmitting || isLoading}
            validate={subscription}
            labelField="name"
            identityKey="id"
            placeholder="Select Subscription"
            isClearable={true}
            clearValue={initialDevice}
          />
        </StyledAutocomplete>
      </NoSsr>

      <br />
      <br />

      <StyledTitle>DEVICE PARAMETERS</StyledTitle>
      <br />
      <Typography variant="body1">Payload Formats</Typography>
      <br />
      <Grid container>
        <Grid item xs={12} sm={10}>
          <StyledPayloadFormatsCard>
            <CardContent>
              <Grid container wrap="nowrap" spacing={8}>
                <Grid item>
                  <Help color="primary" />
                </Grid>
                <Grid item xs>
                  <Typography color="primary">
                    {`Payload format contains one or more payload parameters with format: <ParameterName>:<DataType>:[OptionalValue]`}
                    <br />
                    {`For example: “PayloadType:uint8:0 PressureRaw:int16: TemperatureRaw:int16: ChargeLevelRaw:uint16_le:”`}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </StyledPayloadFormatsCard>
        </Grid>
      </Grid>
      <br />
      <FieldArray name="payloadFormats" render={DevicePayloadFormatFormArray} />
      <br />
      <br />

      <Typography variant="body1">Downlink Formats</Typography>
      <br />
      <FieldArray
        name="downlinkFormats"
        render={DeviceDownlinkFormatFormArray}
      />
      <br />
      <br />

      <Typography variant="body1">Payload Parameters</Typography>
      <br />
      <FieldArray
        name="payloadFieldProperties"
        render={DevicePayloadFieldParametersFormArray}
      />
      <br />
      <br />

      <Typography variant="body1">Value Parameters</Typography>
      <br />
      <FieldArray
        name="valueParameters"
        render={DeviceValueParametersFormArray}
      />
      <br />
      <br />

      <Typography variant="body1">Calculated Parameters</Typography>
      <br />
      <FieldArray
        name="basicCalculationProperties"
        render={DeviceBasicCalculationParametersFormArray}
      />
      {(isSubmitting || isLoading) && <LinearProgress />}

      <br />
      <Grid
        container={true}
        item={true}
        direction="row"
        justify="flex-start"
        alignItems="center">
        <Button
          variant="contained"
          color="primary"
          disabled={isSubmitting || isLoading}
          onClick={submitForm}>
          Create
        </Button>
      </Grid>
    </Form>
  );
};
